import { useMutation } from "@tanstack/react-query";

import i18n, { isSupportedLanguage } from "@/i18n/config";
import { auth, ensureFirebaseInitialized } from "@/lib/firebase";
import { AppRoutes } from "@/routes";

// Firebase Auth localizes its built-in emails (sign-in link, verification)
// to whatever `auth.languageCode` is set to when the email is triggered.
// Keep it in sync with the active i18next language so the sign-in email
// matches the language the user is browsing the app in.
const syncAuthLanguage = () => {
  if (!auth) return;
  const lang = i18n.language?.split("-")[0]?.toLowerCase() ?? "en";
  auth.languageCode = isSupportedLanguage(lang) ? lang : "en";
};

// Mirrors the iOS keychain key in AuthService.swift so the two clients have
// symmetric local-storage semantics, even though they never share storage.
const EMAIL_STORAGE_KEY = "baiki.auth.lastEmailRequestedFor";

const readStoredEmail = (): string | null => {
  try {
    return window.localStorage.getItem(EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredEmail = (email: string) => {
  try {
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  } catch {
    // localStorage can throw in private modes; the redeem flow has a
    // cross-device fallback that re-prompts for the email, so a silent
    // drop is safe.
  }
};

const clearStoredEmail = () => {
  try {
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const useRequestSignInLink = () => {
  return useMutation({
    mutationFn: async (rawEmail: string) => {
      const email = rawEmail.trim();
      await ensureFirebaseInitialized();
      if (!auth) {
        throw new Error("Firebase Auth not initialized");
      }

      syncAuthLanguage();
      const { sendSignInLinkToEmail } = await import("firebase/auth");
      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}${AppRoutes.EMAIL_LINK()}`,
        handleCodeInApp: true,
      });
      writeStoredEmail(email);
      return email;
    },
  });
};

const useCompleteSignInLink = () => {
  return useMutation({
    mutationFn: async ({ email, url }: { email: string; url: string }) => {
      await ensureFirebaseInitialized();
      if (!auth) {
        throw new Error("Firebase Auth not initialized");
      }

      const { isSignInWithEmailLink, signInWithEmailLink } =
        await import("firebase/auth");
      if (!isSignInWithEmailLink(auth, url)) {
        throw new Error("not-a-sign-in-link");
      }

      const credential = await signInWithEmailLink(auth, email.trim(), url);
      clearStoredEmail();
      return credential.user;
    },
  });
};

const useHandleSignOut = () => {
  return useMutation({
    mutationFn: async () => {
      await ensureFirebaseInitialized();
      if (!auth) {
        throw new Error("Firebase Auth not initialized");
      }

      const { signOut } = await import("firebase/auth");
      await signOut(auth);
      clearStoredEmail();
    },
  });
};

export const AuthQueries = {
  EMAIL_STORAGE_KEY,
  readStoredEmail,
  useCompleteSignInLink,
  useHandleSignOut,
  useRequestSignInLink,
};
